import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import Loader from '../../components/common/Loader';
import NoData from '../../components/common/NoData';
import { getLogoByDomainAndAlt } from '../../utils/logoUtils';
import { getPublicSurvey, submitPublicSurvey } from '../../services/userService';

function PublicSurveyFill() {
  const { slug } = useParams();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [survey, setSurvey] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const [respondentName, setRespondentName] = useState('');
  const [respondentEmail, setRespondentEmail] = useState('');

  const [answers, setAnswers] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});

  const collectName = Boolean(survey?.collect_name);
  const collectEmail = Boolean(survey?.collect_email);

  const totalQuestions = questions.length;
  const answeredCount = useMemo(() => {
    let count = 0;
    for (const q of questions) {
      const a = answers[q.id];
      if (!a) continue;
      if (q.type === 'text') {
        if (String(a.answer_text || '').trim()) count += 1;
      } else if (q.type === 'single_choice') {
        if (Number(a.selected_option_id)) count += 1;
      } else {
        if (Array.isArray(a.selected_option_ids) && a.selected_option_ids.length > 0) count += 1;
      }
    }
    return count;
  }, [answers, questions]);

  const progress = totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0;

  const getDomainKey = () => {
    try {
      const raw = String(window.location.hostname || '').toLowerCase();
      const noWww = raw.replace(/^www\./, '');
      return noWww.replace(/^(stage|staging|dev|qa|test|uat)\./, '');
    } catch {
      return '';
    }
  };

  const buildOrgLookupPayload = () => {
    let origin = '';
    let hostname = '';
    try {
      origin = String(window.location.origin || '');
    } catch {
      origin = '';
    }
    try {
      hostname = String(window.location.hostname || '');
    } catch {
      hostname = '';
    }
    return {
      domain: origin,
      hostname,
      domain_key: getDomainKey(),
    };
  };

  const fetchSurvey = async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await getPublicSurvey(slug, buildOrgLookupPayload());
      if (!resp?.success) {
        setError(resp?.message || 'Survey not available');
        setSurvey(null);
        setQuestions([]);
        return;
      }
      setSurvey(resp.data.survey);
      setQuestions(resp.data.questions || []);
    } catch (e) {
      setError(e?.response?.data?.message || 'Survey not available');
      setSurvey(null);
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      setError('Survey link is invalid');
      return;
    }
    fetchSurvey();
  }, [slug]);

  const setAnswer = (questionId, payload) => {
    setAnswers((prev) => ({ ...prev, [questionId]: payload }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
  };

  const validate = () => {
    const nextErrors = {};
    for (const q of questions) {
      if (!q.is_required) continue;
      const a = answers[q.id];
      if (!a) {
        nextErrors[q.id] = 'Required';
        continue;
      }
      if (q.type === 'text') {
        if (!String(a.answer_text || '').trim()) nextErrors[q.id] = 'Required';
      } else if (q.type === 'single_choice') {
        if (!Number(a.selected_option_id)) nextErrors[q.id] = 'Required';
      } else {
        if (!Array.isArray(a.selected_option_ids) || a.selected_option_ids.length === 0) nextErrors[q.id] = 'Required';
      }
    }
    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildAnswersArray = () => {
    return questions
      .map((q) => {
        const a = answers[q.id];
        if (!a) return null;
        if (q.type === 'text') return { question_id: q.id, answer_text: a.answer_text || '' };
        if (q.type === 'single_choice') return { question_id: q.id, selected_option_id: a.selected_option_id };
        return { question_id: q.id, selected_option_ids: a.selected_option_ids || [] };
      })
      .filter(Boolean);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error('Please fill required questions');
      return;
    }

    setSubmitting(true);
    try {
      const resp = await submitPublicSurvey(slug, {
        ...buildOrgLookupPayload(),
        respondent_name: respondentName || null,
        respondent_email: respondentEmail || null,
        answers: buildAnswersArray(),
      });

      if (!resp?.success) {
        toast.error(resp?.message || 'Failed to submit survey');
        return;
      }

      setDone(true);
      toast.success('Thanks! Your response has been recorded.');
    } catch (e2) {
      const msg = e2?.response?.data?.message || 'Failed to submit survey';
      toast.error(msg);
      if (e2?.response?.data?.errors) {
        const apiErrors = {};
        for (const er of e2.response.data.errors) {
          apiErrors[er.question_id] = er.message || 'Required';
        }
        setFieldErrors(apiErrors);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Loader message="Loading survey..." />;

  if (error) {
    return <NoData message={error} />;
  }

  if (!survey) {
    return <NoData message="Survey not available" />;
  }

  if (done) {
    return (
      <div className="min-h-[calc(100vh-2rem)] flex items-center justify-center py-10">
        <div className="w-full max-w-xl bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="text-center">
            <div className="mb-4 flex justify-center">
              <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center">
                <Icon icon="fluent:checkmark-circle-24-filled" width="28" height="28" className="text-emerald-600" />
              </div>
            </div>
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Response submitted</h1>
            <p className="text-gray-500 text-sm mt-2">Thank you for taking the time to complete this survey.</p>
          </div>
        </div>
        <div className="mt-6 text-center text-xs text-gray-500">
          Powered by{' '}
          <a
            href="https://healthchip.in"
            target="_blank"
            rel="noreferrer"
            className="font-semibold text-gray-700 hover:text-gray-900"
          >
            HealthChip
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="text-center mb-6">
          <img
            src={getLogoByDomainAndAlt().darkLogo}
            alt={getLogoByDomainAndAlt().alt}
            className="h-12 max-w-[200px] mx-auto object-contain"
          />
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 break-words">{survey.title}</h1>
                {survey.description ? (
                  <p className="text-gray-500 text-sm mt-2 whitespace-pre-wrap">{survey.description}</p>
                ) : null}
              </div>
              <div className="text-right">
                <div className="text-xs text-gray-500">Progress</div>
                <div className="text-sm font-semibold text-gray-900">{progress}%</div>
              </div>
            </div>

            <div className="mt-4">
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-2 bg-primary" style={{ width: `${progress}%` }} />
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {collectName || collectEmail ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {collectName ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name (optional)</label>
                    <input
                      value={respondentName}
                      onChange={(e) => setRespondentName(e.target.value)}
                      className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Your name"
                    />
                  </div>
                ) : null}

                {collectEmail ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email (optional)</label>
                    <input
                      value={respondentEmail}
                      onChange={(e) => setRespondentEmail(e.target.value)}
                      className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Your email"
                    />
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="space-y-5">
              {questions.map((q, idx) => {
                const err = fieldErrors[q.id];
                return (
                  <div key={q.id} className={`rounded-2xl border ${err ? 'border-red-200 bg-red-50/30' : 'border-gray-100'} p-4`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-900 break-words">
                          {idx + 1}. {q.question_text}
                          {q.is_required ? <span className="text-red-500"> *</span> : null}
                        </div>
                      </div>
                      {err ? (
                        <div className="text-xs text-red-600 font-medium">{err}</div>
                      ) : null}
                    </div>

                    {q.type === 'text' ? (
                      <textarea
                        className="mt-3 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                        rows={3}
                        value={answers[q.id]?.answer_text || ''}
                        onChange={(e) => setAnswer(q.id, { answer_text: e.target.value })}
                        placeholder="Type your answer..."
                      />
                    ) : null}

                    {q.type === 'single_choice' ? (
                      <div className="mt-3 space-y-2">
                        {(q.options || []).map((o) => {
                          const checked = Number(answers[q.id]?.selected_option_id) === Number(o.id);
                          return (
                            <label
                              key={o.id}
                              className={`flex items-center gap-3 rounded-xl border px-3 py-2 cursor-pointer transition ${
                                checked ? 'border-primary bg-primary/5' : 'border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              <input
                                type="radio"
                                name={`q_${q.id}`}
                                checked={checked}
                                onChange={() => setAnswer(q.id, { selected_option_id: o.id })}
                              />
                              <span className="text-sm text-gray-800">{o.option_text}</span>
                            </label>
                          );
                        })}
                      </div>
                    ) : null}

                    {q.type === 'multi_choice' ? (
                      <div className="mt-3 space-y-2">
                        {(q.options || []).map((o) => {
                          const arr = Array.isArray(answers[q.id]?.selected_option_ids)
                            ? answers[q.id].selected_option_ids
                            : [];
                          const checked = arr.includes(o.id);
                          return (
                            <label
                              key={o.id}
                              className={`flex items-center gap-3 rounded-xl border px-3 py-2 cursor-pointer transition ${
                                checked ? 'border-primary bg-primary/5' : 'border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => {
                                  const next = checked ? arr.filter((x) => x !== o.id) : [...arr, o.id];
                                  setAnswer(q.id, { selected_option_ids: next });
                                }}
                              />
                              <span className="text-sm text-gray-800">{o.option_text}</span>
                            </label>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-between gap-3 pt-2">
              <div className="text-xs text-gray-500">
                {answeredCount} of {totalQuestions} answered
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 rounded-xl bg-primary text-white px-4 py-2 text-sm font-semibold hover:opacity-95 disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Submitting
                  </>
                ) : (
                  <>
                    <Icon icon="fluent:send-24-regular" width="18" height="18" />
                    Submit
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        <div className="text-center text-xs text-gray-400 mt-6">Powered by <a href="https://healthchip.in" target="_blank" rel="noopener noreferrer">HealthChip</a></div>
      </div>
    </div>
  );
}

export default PublicSurveyFill;
